import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { API_CONFIG, getNodeApiHeaders } from '../helpers/constants';
import { getOffice365AdminConsentUrl } from '../helpers/office365AdminConsent';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  email_id?: string;
}

/** Error codes from Microsoft when org requires admin consent */
const ADMIN_CONSENT_ERROR_CODES = ['consent_required', 'access_denied', 'interaction_required'];

const Office365OAuthCallback: React.FC = () => {
  const muiTheme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAdminConsent, setNeedsAdminConsent] = useState(false);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const adminConsentUrl = getOffice365AdminConsentUrl();

  // Email 401-reauth flow (revoked refresh token) reuses this callback route for Outlook
  // accounts, mirroring GoogleOAuthCallback.tsx's emailReauthAddress handling — see
  // docs/EmailReauth_401_Recovery_FE_Implementation.md §4.3.
  const emailReauthAddress = useMemo(() => {
    const state = searchParams.get('state');
    if (state && state.startsWith('email_reauth|')) {
      try {
        const b64 = state.split('|')[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        return atob(padded);
      } catch {
        return null;
      }
    }
    return null;
  }, [searchParams]);

  const handleCopyAdminLink = useCallback(() => {
    navigator.clipboard.writeText(adminConsentUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  }, [adminConsentUrl]);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const oauthError = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (oauthError) {
          // Treat consent/access-denied as "admin approval needed" for seamless SaaS flow
          if (ADMIN_CONSENT_ERROR_CODES.includes(oauthError)) {
            setNeedsAdminConsent(true);
            setError(null);
            // Notify the opener so it stops showing a "waiting for sign-in" state (PR #594
            // review, low-severity — previously nothing reached the opener at all here, unlike
            // GoogleOAuthCallback.tsx's error branch). Deliberately don't close the popup: the
            // admin-consent UI below is the point of this branch — the user still needs to copy
            // the link and send it to their admin.
            if (window.opener) {
              window.opener.postMessage(
                {
                  type: 'OAUTH_CANCELLED',
                  error: oauthError,
                  errorDescription: errorDescription || undefined,
                  needsAdminConsent: true,
                  ...(emailReauthAddress ? { emailReauthAddress } : {}),
                },
                window.location.origin
              );
            }
          } else {
            setError(`OAuth error: ${oauthError}${errorDescription ? ` - ${errorDescription}` : ''}`);
            // A real denial (not an admin-consent gate) — nothing more for the user to do in this
            // popup, so notify the opener and close it, mirroring GoogleOAuthCallback.tsx's error
            // branch instead of leaving the opener hanging indefinitely.
            if (window.opener) {
              window.opener.postMessage(
                {
                  type: 'OAUTH_CANCELLED',
                  error: oauthError,
                  errorDescription: errorDescription || undefined,
                  ...(emailReauthAddress ? { emailReauthAddress } : {}),
                },
                window.location.origin
              );
              window.close();
              return;
            }
          }
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setLoading(false);
          return;
        }

        // Construct redirectUri based on current origin
        const redirectUri = `${window.location.origin}/auth/office365/callback`;

        // Call the server endpoint to exchange code for tokens
        const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/generateToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getNodeApiHeaders(),
          },
          body: JSON.stringify({ code, redirectUri }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tokenResponse: TokenResponse = await response.json();
        setTokenData(tokenResponse);
        setLoading(false);

        // Send data back to parent window and close
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            data: {
              provider: 'office365',
              ...tokenResponse,
              email_id: tokenResponse.email_id || 'user@example.com', // This should come from the API response
              ...(emailReauthAddress ? { emailReauthAddress } : {}),
            }
          }, window.location.origin);
          window.close();
        }
      } catch (err) {
        console.error('Error processing Office 365 OAuth callback:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, [searchParams]);

  const handleGoBack = () => {
    // If this callback is running inside a popup (opened by AddSpaceModal),
    // close the popup so the user lands back on the channel screen they came from.
    // If it was a full-page redirect (no opener), fall back to the support space.
    if (window.opener) {
      window.close();
    } else {
      navigate('/supportSpace');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Authenticating to Office 365...
        </Typography>
      </Box>
    );
  }

  // If we have a parent window, don't show the success screen - just close
  if (window.opener && !error && !needsAdminConsent) {
    return null;
  }

  // Admin consent required – show one-time approval link for SaaS users
  if (needsAdminConsent) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 560,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom color="primary">
            One-time admin approval needed
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            Your organization requires an admin to approve this app once. After that, you and your colleagues can connect Outlook without seeing this again.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            Send this link to your IT admin to approve:
          </Typography>
          <Box
            sx={{
              p: 1.5,
              bgcolor:
                muiTheme.palette.mode === 'dark'
                  ? muiTheme.palette.grey[800]
                  : muiTheme.palette.grey[100],
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              wordBreak: 'break-all',
              textAlign: 'left',
              mb: 2,
            }}
          >
            <Typography variant="caption" component="pre" sx={{ m: 0, color: 'text.primary' }}>
              {adminConsentUrl}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyAdminLink}
            >
              {copySuccess ? 'Copied!' : 'Copy link'}
            </Button>
            <Button
              variant="outlined"
              href={`mailto:?subject=Approve Outlook connection for Prism&body=Please approve our Outlook integration by opening this link (sign in as admin):%0A%0A${encodeURIComponent(adminConsentUrl)}`}
            >
              Email link to admin
            </Button>
          </Box>
          <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            After your admin approves, click below to try connecting again.
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.close()}>
            Close and try again
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={handleGoBack}>
              Go Back
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              ✅ Office 365 Authentication Successful!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your Office 365 account has been successfully authenticated.
            </Typography>

            <Button variant="contained" onClick={handleGoBack}>
              Continue to Support Spaces
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Office365OAuthCallback;
